/* eslint-disable @typescript-eslint/no-explicit-any */
export default class LoginWithAmazon {
  private clientId: string;

  constructor(clientId: string) {
    this.clientId = clientId;
  }

  protected clearSession() {
    sessionStorage.removeItem('lwa-oauth-state');
    sessionStorage.removeItem('lwa-oauth-code-verifier');
    sessionStorage.removeItem('lwa-oauth-redirect-uri');
  }

  async authorize(redirect_uri: string) {
    const state = this.generateState();
    const codeVerifier = this.generateCodeVerifier();
    const code_challenge = await this.generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('lwa-oauth-state', state);
    sessionStorage.setItem('lwa-oauth-code-verifier', codeVerifier);
    sessionStorage.setItem('lwa-oauth-redirect-uri', redirect_uri);

    const search = new URLSearchParams({
      scope: 'profile',
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri,
      state,
      code_challenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `https://www.amazon.com/ap/oa?${search.toString()}`;
    return;
  }

  retriveCode() {
    return new Promise<{
      code?: string;
      scope?: string;
      error?: string;
    }>((res, rej) => {
      const search = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(search);
      const state = sessionStorage.getItem('lwa-oauth-state');

      if (params.state && params.error) {
        return rej(new Error(params.error));
      }

      if (params.state && params.state !== state) {
        return rej(new Error('Invalid state code'));
      }

      if (params.code) {
        res(params);

        const url = new URL(window.location as any);
        url.search = '';
        history.replaceState(null, '', url.toString());
        return;
      }
    });
  }

  retriveAccessToken(code: string) {
    return new Promise<{
      access_token: string;
      token_type: string;
      expires_in: number;
    }>(async (res, rej) => {
      const redirect_uri = sessionStorage.getItem(
        'lwa-oauth-redirect-uri',
      ) as string;
      const code_verifier = sessionStorage.getItem(
        'lwa-oauth-code-verifier',
      ) as string;

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri,
        client_id: this.clientId,
        code_verifier,
      });

      await fetch('https://api.amazon.com/auth/o2/token', {
        method: 'POST',
        headers: new Headers({
          'Content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
        }),
        body,
      })
        .then((data) => res(data.json()))
        .catch(rej);

      this.clearSession();
    });
  }

  retriveProfile(accessToken: string) {
    return new Promise<{
      user_id: string;
      email: string;
      name: string;
    }>(async (res, rej) => {
      await fetch('https://api.amazon.com/user/profile', {
        method: 'GET',
        headers: new Headers({ Authorization: 'Bearer  ' + accessToken }),
      })
        .then((data) => res(data.json()))
        .catch(rej);
    });
  }

  private generateState() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }

  generateCodeVerifier() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const base64UrlSafe = btoa(String.fromCharCode.apply(null, array as any))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64UrlSafe;
  }

  async generateCodeChallenge(codeVerifier: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    const hash = await crypto.subtle.digest('SHA-256', data);
    const base64UrlSafe = btoa(
      String.fromCharCode.apply(null, new Uint8Array(hash) as any),
    )
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64UrlSafe;
  }

  static Errors: Record<string, string> = {
    invalid_request:
      'A solicitação não possui um parâmetro obrigatório, tem um valor inválido ou foi formada de outra forma incorretamente.',
    unauthorized_client:
      'O cliente não está autorizado a solicitar um código de autorização.',
    access_denied:
      'O proprietário do recurso ou servidor de autorização negou esta solicitação.',
    unsupported_response_type:
      'A solicitação especificou um tipo de resposta não compatível. Para este cenário, o response_typedeve ser code.',
    invalid_scope: 'O cliente solicitou o escopo errado.',
    server_error:
      'O servidor de autorização encontrou um erro inesperado (tratado como um erro 500 Internal Server HTTP).',
    temporarily_unavailable:
      'O servidor de autorização está indisponível no momento devido a uma sobrecarga temporária ou manutenção programada (trate como um erro HTTP 503 Serviço Indisponível).',
  };
}
